const STORAGE_KEY = "flexspace.sticky_promo_code";

// Generous safety net so a forgotten code/email can't auto-apply forever. The
// rehydration site already mismatches if the promotion was rotated to a new
// code, so this only matters for promotions disabled outright (active code
// becomes undefined → no rehydration anyway). 30d covers typical campaign
// windows comfortably.
const STICKY_PROMO_TTL_DAYS = 30;
const MAX_AGE_MS = STICKY_PROMO_TTL_DAYS * 24 * 60 * 60 * 1000;

// One stored entry per browser: the most recent discount-popup signup. Holds
// both the promo code (partner-scoped — different partners run different codes)
// and the email (user identity — not partner-scoped). They're a single signup
// event, so they share lifecycle: written together at signup, cleared together
// on redeem.
//
// email is optional for backward compat with entries written before the email
// rollout. Legacy entries continue to auto-apply the promo on return; only the
// email prefill no-ops until the 30-day TTL ages them out or the user signs up
// again.
interface StoredPromoCode {
  partnerId: string;
  code: string;
  email?: string;
  savedAt: number;
}

export function readStickyPromoCode(partnerId: string): string | null {
  const entry = readEntry();
  if (!entry) return null;
  if (entry.partnerId !== partnerId) return null;
  return entry.code;
}

// Email is the user's identity, not partner-scoped — returned regardless of
// which partner the signup originally happened on. Caller decides whether to
// prefill (e.g. only when the form is empty).
export function readStickySignupEmail(): string | null {
  const entry = readEntry();
  return entry?.email ?? null;
}

export function writeStickyPromoCode(
  partnerId: string,
  code: string,
  email: string
): void {
  const entry: StoredPromoCode = {
    partnerId,
    code,
    email,
    savedAt: Date.now(),
  };
  safeSetItem(STORAGE_KEY, JSON.stringify(entry));
}

export function clearStickyPromoCode(): void {
  safeRemoveItem(STORAGE_KEY);
}

function readEntry(): StoredPromoCode | null {
  const raw = safeGetItem(STORAGE_KEY);
  if (!raw) return null;

  const parsed = safeParse(raw);
  if (!parsed) return null;
  if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;

  return parsed;
}

function safeParse(raw: string): StoredPromoCode | null {
  try {
    const value = JSON.parse(raw) as Partial<StoredPromoCode>;
    if (
      typeof value.partnerId !== "string" ||
      typeof value.code !== "string" ||
      typeof value.savedAt !== "number"
    ) {
      return null;
    }
    // email is optional — legacy entries (pre-email rollout) are still valid
    // for promo rehydration; readStickySignupEmail returns null for them.
    return value as StoredPromoCode;
  } catch {
    return null;
  }
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Safari private mode / quota exceeded — silently no-op.
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Safari private mode — silently no-op.
  }
}
